/**
 * Per-app USER PROFILE operator UI (access-model slice 4).
 *
 * Two surfaces:
 *  - `ProfileFieldsSection` — declare the app's member-attribute SCHEMA
 *    (`app_profile_fields`). A LOCKED field (the default) is operator-only and
 *    may back an RLS `$user.<key>` row filter; an editable one is a self-serve
 *    portal preference. The lock state is the security keystone, surfaced
 *    prominently.
 *  - `MemberAttrsDialog` — set ONE member's attribute VALUES. The operator may
 *    set ANY declared field (incl. locked / RLS-bearing); only the portal
 *    self-service surface is restricted to editable fields.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { IdCard, Lock, LockOpen, Plus, Trash2 } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import {
  appsApi,
  type MemberOut,
  type ProfileField,
  type ProfileFieldDataType,
} from "@/lib/api-apps";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmptyState } from "@/components/ui/page";
import { Switch } from "@/components/ui/switch";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";

const DATA_TYPES: ProfileFieldDataType[] = ["text", "number", "select"];

const selectClass =
  "block w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm";

function slugifyKey(label: string): string {
  return label
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^[^a-z]+/, "")
    .replace(/_+$/g, "");
}

// ---------------------------------------------------------------------------
// Field schema editor
// ---------------------------------------------------------------------------

export function ProfileFieldsSection({
  appId,
  canWrite,
}: {
  appId: string;
  canWrite: boolean;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = React.useState(false);

  const fieldsQ = useQuery({
    queryKey: ["apps", appId, "profile-fields"],
    queryFn: () => appsApi.listProfileFields(appId),
  });
  const fields = fieldsQ.data ?? [];

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["apps", appId, "profile-fields"] });

  const toggleEditable = useMutation({
    mutationFn: ({ id, user_editable }: { id: string; user_editable: boolean }) =>
      appsApi.updateProfileField(appId, id, { user_editable }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => appsApi.deleteProfileField(appId, id),
    onSuccess: invalidate,
  });

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
          <IdCard className="h-4 w-4" strokeWidth={1.5} />
          {t("apps.profileFields", "Profile fields")}
        </h3>
        {canWrite && (
          <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4" strokeWidth={1.5} />
            {t("apps.newProfileField", "New field")}
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        {t(
          "apps.profileFieldsHint",
          "Per-member attributes (e.g. area, agent). A locked field is operator-only and can drive row security ($user.<key>); an editable one is a portal-user preference.",
        )}
      </p>
      {fields.length === 0 && !fieldsQ.isLoading ? (
        <EmptyState
          icon={IdCard}
          title={t("apps.noProfileFields", "No profile fields")}
          description={t(
            "apps.noProfileFieldsDescription",
            "Declare attributes your members carry, then set their values per member.",
          )}
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-border/60">
          <Table>
            <Thead>
              <Tr>
                <Th>{t("apps.profileFieldLabel", "Field")}</Th>
                <Th className="w-32">{t("apps.profileFieldType", "Type")}</Th>
                <Th className="w-40">{t("apps.profileFieldAccess", "Editable by user")}</Th>
                {canWrite && (
                  <Th className="w-16 text-right">{t("common.actions", "Actions")}</Th>
                )}
              </Tr>
            </Thead>
            <Tbody>
              {fields.map((f) => (
                <Tr key={f.id}>
                  <Td>
                    <div className="font-medium">{f.label}</div>
                    <div className="font-mono text-[10px] text-muted-foreground">
                      $user.{f.key}
                    </div>
                  </Td>
                  <Td className="text-xs text-muted-foreground">
                    {f.data_type}
                    {f.data_type === "select" && f.options && (
                      <span className="ml-1">({f.options.join(", ")})</span>
                    )}
                  </Td>
                  <Td>
                    {canWrite ? (
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={f.user_editable}
                          onCheckedChange={(v) =>
                            toggleEditable.mutate({ id: f.id, user_editable: v })
                          }
                          aria-label={t("apps.profileFieldEditableToggle", "Editable by the user")}
                        />
                        {f.user_editable ? (
                          <Badge tone="warning" className="gap-1 text-[10px]">
                            <LockOpen className="h-3 w-3" strokeWidth={1.5} />
                            {t("apps.profileEditable", "editable")}
                          </Badge>
                        ) : (
                          <Badge tone="neutral" className="gap-1 text-[10px]">
                            <Lock className="h-3 w-3" strokeWidth={1.5} />
                            {t("apps.profileLocked", "locked")}
                          </Badge>
                        )}
                      </div>
                    ) : f.user_editable ? (
                      t("apps.profileEditable", "editable")
                    ) : (
                      t("apps.profileLocked", "locked")
                    )}
                  </Td>
                  {canWrite && (
                    <Td className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={t("common.delete", "Delete")}
                        onClick={() => {
                          if (
                            confirm(
                              t("apps.confirmDeleteProfileField", 'Delete field "{{name}}"?', {
                                name: f.label,
                              }),
                            )
                          ) {
                            remove.mutate(f.id);
                          }
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-danger" strokeWidth={1.5} />
                      </Button>
                    </Td>
                  )}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </div>
      )}
      <NewProfileFieldDialog appId={appId} open={open} onOpenChange={setOpen} />
    </section>
  );
}

function NewProfileFieldDialog({
  appId,
  open,
  onOpenChange,
}: {
  appId: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [label, setLabel] = React.useState("");
  const [key, setKey] = React.useState("");
  const [keyTouched, setKeyTouched] = React.useState(false);
  const [dataType, setDataType] = React.useState<ProfileFieldDataType>("text");
  const [optionsText, setOptionsText] = React.useState("");
  const [userEditable, setUserEditable] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setLabel("");
      setKey("");
      setKeyTouched(false);
      setDataType("text");
      setOptionsText("");
      setUserEditable(false);
    }
  }, [open]);

  const create = useMutation({
    mutationFn: () =>
      appsApi.createProfileField(appId, {
        key,
        label,
        data_type: dataType,
        options:
          dataType === "select"
            ? optionsText.split(/[\n,]/).map((s) => s.trim()).filter(Boolean)
            : null,
        user_editable: userEditable,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["apps", appId, "profile-fields"] });
      onOpenChange(false);
    },
  });

  const effectiveKey = keyTouched ? key : slugifyKey(label);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("apps.newProfileField", "New field")}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <div>
            <Label htmlFor="pf-label">{t("apps.profileFieldLabel", "Field")}</Label>
            <Input
              id="pf-label"
              value={label}
              onChange={(e) => {
                setLabel(e.target.value);
                if (!keyTouched) setKey(slugifyKey(e.target.value));
              }}
              placeholder="Area"
            />
          </div>
          <div>
            <Label htmlFor="pf-key">{t("apps.profileFieldKey", "Key ($user.<key>)")}</Label>
            <Input
              id="pf-key"
              value={effectiveKey}
              onChange={(e) => {
                setKeyTouched(true);
                setKey(e.target.value);
              }}
              className="font-mono"
              placeholder="area"
            />
          </div>
          <div>
            <Label htmlFor="pf-type">{t("apps.profileFieldType", "Type")}</Label>
            <select
              id="pf-type"
              value={dataType}
              className={selectClass}
              onChange={(e) => setDataType(e.target.value as ProfileFieldDataType)}
            >
              {DATA_TYPES.map((dt) => (
                <option key={dt} value={dt}>
                  {dt}
                </option>
              ))}
            </select>
          </div>
          {dataType === "select" && (
            <div>
              <Label htmlFor="pf-options">{t("apps.profileFieldOptions", "Options")}</Label>
              <textarea
                id="pf-options"
                value={optionsText}
                onChange={(e) => setOptionsText(e.target.value)}
                className={`${selectClass} h-20`}
                placeholder={t("apps.profileFieldOptionsHint", "One per line (or comma-separated)")}
              />
            </div>
          )}
          <label className="flex items-start gap-2 text-sm">
            <Switch
              checked={userEditable}
              onCheckedChange={setUserEditable}
              aria-label={t("apps.profileFieldEditableToggle", "Editable by the user")}
            />
            <span>
              {t("apps.profileFieldEditableLabel", "Editable by the portal user")}
              <span className="block text-xs text-muted-foreground">
                {t(
                  "apps.profileFieldEditableHelp",
                  "Leave OFF (locked) for any attribute used in row security — an editable field can't back an RLS rule.",
                )}
              </span>
            </span>
          </label>
          {create.isError && (
            <Alert tone="danger" title={t("apps.createProfileFieldFailed", "Could not create field")}>
              {String((create.error as Error)?.message ?? "")}
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            disabled={!label.trim() || !effectiveKey.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            {t("apps.create", "Create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Per-member attribute values
// ---------------------------------------------------------------------------

export function MemberAttrsDialog({
  appId,
  member,
  open,
  onOpenChange,
}: {
  appId: string;
  member: MemberOut | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const fieldsQ = useQuery({
    queryKey: ["apps", appId, "profile-fields"],
    queryFn: () => appsApi.listProfileFields(appId),
    enabled: open,
  });
  const fields = fieldsQ.data ?? [];
  const [values, setValues] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    if (open && member) {
      const init: Record<string, string> = {};
      for (const f of fields) {
        const v = (member.attrs ?? {})[f.key];
        init[f.key] = v === undefined || v === null ? "" : String(v);
      }
      setValues(init);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, member?.id, fieldsQ.data]);

  const save = useMutation({
    mutationFn: () => {
      const attrs: Record<string, unknown> = {};
      for (const f of fields) {
        const v = (values[f.key] ?? "").trim();
        if (v !== "") attrs[f.key] = v;
      }
      return appsApi.updateMemberAttrs(appId, member!.id, attrs);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["apps", appId, "members"] });
      onOpenChange(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("apps.editMemberProfile", "Profile")} — {member?.principal_label ?? member?.principal_id}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          {fields.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("apps.noProfileFieldsYet", "Declare profile fields first to set their values.")}
            </p>
          ) : (
            fields.map((f) => (
              <div key={f.id}>
                <Label htmlFor={`attr-${f.id}`} className="flex items-center gap-2">
                  {f.label}
                  {!f.user_editable && (
                    <Lock className="h-3 w-3 text-muted-foreground" strokeWidth={1.5} />
                  )}
                </Label>
                <AttrInput
                  id={`attr-${f.id}`}
                  field={f}
                  value={values[f.key] ?? ""}
                  onChange={(v) => setValues((prev) => ({ ...prev, [f.key]: v }))}
                />
              </div>
            ))
          )}
          {save.isError && (
            <Alert tone="danger" title={t("apps.saveProfileFailed", "Could not save profile")}>
              {String((save.error as Error)?.message ?? "")}
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button disabled={fields.length === 0 || save.isPending} onClick={() => save.mutate()}>
            {t("common.save", "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AttrInput({
  id,
  field,
  value,
  onChange,
}: {
  id: string;
  field: ProfileField;
  value: string;
  onChange: (v: string) => void;
}) {
  const { t } = useTranslation();
  if (field.data_type === "select") {
    return (
      <select
        id={id}
        value={value}
        className={selectClass}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{t("apps.profileValueNone", "— none —")}</option>
        {(field.options ?? []).map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    );
  }
  return (
    <Input
      id={id}
      type={field.data_type === "number" ? "number" : "text"}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}
